import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ContactUs.css';
import { FaCheckCircle, FaClock, FaComment, FaEnvelope, FaExclamationCircle, FaFileContract, FaGithub, FaInfoCircle, FaInstagram, FaLinkedinIn, FaPaperPlane, FaQuestionCircle } from 'react-icons/fa';

const ContactUs = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Form validation and submission states
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear error for this field if exists
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    // Set errors and return if form is valid
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Start submission
    setIsSubmitting(true);
    setSubmitError('');
    
    // Mock submission - replace with actual API call in production
    setTimeout(() => {
      // Simulate successful submission
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
    
    // For error simulation (uncomment to test error state)
    /*
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitError('There was an error sending your message. Please try again later.');
    }, 1500);
    */
  };

  // Handle new message after success
  const handleNewMessage = () => {
    setSubmitSuccess(false);
    setSubmitError('');
  };

  return (
    <div className="contact-page">
      {/* SVG Definitions for Patterns */}
      <svg 
        width="0" 
        height="0" 
        style={{ position: 'absolute' }}
      >
        <defs>
          <pattern 
            id="gridPattern" 
            width="20" 
            height="20" 
            patternUnits="userSpaceOnUse"
          >
            <path 
              d="M 20 0 L 0 0 0 20" 
              fill="none" 
              stroke="white" 
              strokeWidth="0.5" 
              opacity="0.3"
            />
          </pattern>
        </defs>
      </svg>

      <div className="contact-hero">
        <div className="contact-hero__overlay">
          <svg 
            width="100%" 
            height="100%" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect 
              width="100%" 
              height="100%" 
              fill="url(#gridPattern)" 
            />
          </svg>
        </div>
        <div className="contact-container">
          <h1 className="contact-hero__title">Contact Us</h1>
          <p className="contact-hero__subtitle">Have questions or feedback? We'd love to hear from you.</p>
        </div>
      </div>

      <div className="contact-container">
        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-card">
              <div className="contact-card__header">
                <h2 className="contact-card__title">Get in Touch</h2>
                <div className="contact-divider"></div>
              </div>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="contact-method__icon">
                    <FaEnvelope />
                  </div>
                  <div className="contact-method__details">
                    <h3 className="contact-method__title">Email</h3>
                    <p className="contact-method__value">
                      <a href="mailto:oppliycvance@gmail.com">oppliycvance@gmail.com</a>
                    </p>
                    <p className="contact-method__description">
                      For general inquiries and support
                    </p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="contact-method__icon">
                    <FaComment />
                  </div>
                  <div className="contact-method__details">
                    <h3 className="contact-method__title">Business Inquiries</h3>
                    <p className="contact-method__value">
                      <a href="mailto:business@oppliycvance@gmail.com">oppliycvance@gmail.com</a>
                    </p>
                    <p className="contact-method__description">
                      For partnerships and collaborations
                    </p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="contact-method__icon">
                    <FaClock />
                  </div>
                  <div className="contact-method__details">
                    <h3 className="contact-method__title">Response Time</h3>
                    <p className="contact-method__description">
                      We aim to respond to all inquiries within 24-48 hours
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="contact-social">
                <h3 className="contact-social__title">Connect With Us</h3>
                <div className="contact-social__links">
                  <a 
                    href="https://linkedin.com/in/venkatasaipunnam" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="contact-social__link"
                    aria-label="LinkedIn"
                  >
                    <FaLinkedinIn />
                  </a>
                  <a 
                    href="https://github.com/venkatasaipunnam" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="contact-social__link"
                    aria-label="GitHub"
                  >
                    <FaGithub />
                  </a>
                  <a 
                    href="https://instagram.com/cvance_oppliy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="contact-social__link"
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </a>
                </div>
              </div>
              
              <div className="contact-links">
                <div className="contact-links__title">Quick Links</div>
                <div className="contact-links__grid">
                  <Link to="/help" className="contact-link">
                    <FaQuestionCircle />
                    <span>Help Center</span>
                  </Link>
                  <Link to="/faq" className="contact-link">
                    <FaInfoCircle />
                    <span>FAQ</span>
                  </Link>
                  <Link to="/about" className="contact-link">
                    <faUser />
                    <span>About Us</span>
                  </Link>
                  <Link to="/terms" className="contact-link">
                    <FaFileContract />
                    <span>Terms</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          <div className="contact-form-wrapper">
            <div className="contact-form-card">
              <div className="contact-card__header">
                <h2 className="contact-card__title">Send a Message</h2>
                <div className="contact-divider"></div>
              </div>
              
              {submitSuccess ? (
                <div className="contact-success">
                  <div className="contact-success__icon">
                    <FaCheckCircle />
                  </div>
                  <h3 className="contact-success__title">Message Sent!</h3>
                  <p className="contact-success__message">
                    Thank you for reaching out. We've received your message and will get back to you shortly.
                  </p>
                  <button 
                    className="contact-button contact-button--primary"
                    onClick={handleNewMessage}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  {submitError && (
                    <div className="contact-form-error">
                      <FaExclamationCircle />
                      <span>{submitError}</span>
                    </div>
                  )}
                  
                  <div className="contact-form-group">
                    <label 
                      htmlFor="name" 
                      className="contact-form-label"
                    >
                      Your Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className={`contact-form-input ${errors.name ? 'has-error' : ''}`}
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      disabled={isSubmitting}
                    />
                    {errors.name && (
                      <div className="contact-form-error-text">{errors.name}</div>
                    )}
                  </div>
                  
                  <div className="contact-form-group">
                    <label 
                      htmlFor="email" 
                      className="contact-form-label"
                    >
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`contact-form-input ${errors.email ? 'has-error' : ''}`}
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <div className="contact-form-error-text">{errors.email}</div>
                    )}
                  </div>
                  
                  <div className="contact-form-group">
                    <label 
                      htmlFor="subject" 
                      className="contact-form-label"
                    >
                      Subject <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className={`contact-form-input ${errors.subject ? 'has-error' : ''}`}
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What is your message about?"
                      disabled={isSubmitting}
                    />
                    {errors.subject && (
                      <div className="contact-form-error-text">{errors.subject}</div>
                    )}
                  </div>
                  
                  <div className="contact-form-group">
                    <label 
                      htmlFor="message" 
                      className="contact-form-label"
                    >
                      Message <span className="required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className={`contact-form-textarea ${errors.message ? 'has-error' : ''}`}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please provide details about your inquiry..."
                      rows={6}
                      disabled={isSubmitting}
                    ></textarea>
                    {errors.message && (
                      <div className="contact-form-error-text">{errors.message}</div>
                    )}
                  </div>
                  
                  <div className="contact-form-submit">
                    <button 
                      type="submit" 
                      className="contact-button contact-button--primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
        
        <section className="contact-disclaimer">
          <div className="disclaimer-content">
            <h3 className="disclaimer-title">Project Status Disclaimer</h3>
            <p className="disclaimer-text">
              CVance and OppLiy are currently in the project development phase. We are not a registered 
              company or LLC at this time. This is a non-profit project aimed at addressing challenges 
              in the job search process. The platforms and services described are under development and 
              subject to change. Any information provided is for informational purposes only and does not 
              constitute a commercial offering or binding agreement.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactUs;